<template>
  <div id="call_routing" class="flex flex-col gap-6 text_color">
    <div
      id="call_routing_header"
      class="flex flex-row justify-between items-center"
    >
      <h1 class="text-xl font-semibold text_color">Call Routing</h1>
      <a-button class="primary_medium" @click="handleCreateRoute"
        >Create Route</a-button
      >
    </div>

    <div id="call_routing_content" class="flex flex-col">
      <SkeletonLoader v-if="isLoading" :page="loaderPages.TABLE" />

      <CustomTable
        v-else-if="callRoutes?.length > 0"
        :headers="callRoutesHeaders"
        :data="callRoutes"
        :pagination="pagination"
        :actions="callRoutesActions"
        :show-actions-column="true"
        custom-action-key="is_active"
        :custom-action-component="CallRoutingToggle"
        @update:pagination="handlePageChange"
      />

      <EmptyScreen v-else source="call_routing" text="No call routes found!" />
    </div>

    <div id="call_routing_actions">
      <CustomModal
        title="Delete Route"
        v-model:visible="showModalToDelete"
        width="30vw"
      >
        <span class="text-base text_color leading-5"
          >Are you sure you want to delete
          <b>{{ routeToManipulate?.name }}</b> route?</span
        >
        <template #footer>
          <a-button class="primary_medium" @click="toDeleteCallRoute"
            >Confirm
          </a-button>
        </template>
      </CustomModal>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { message } from "ant-design-vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { callRoutesHeaders } from "@/src/constants/callConstants.js";
import { useRouter } from "vue-router";

import CallService from "@/src/api/CallService.js";
import CustomModal from "@/src/components/CustomModal.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import CallRoutingToggle from "@/src/components/telephony_components/toggles/CallRoutingToggle.vue";
import { imageNames } from "@/src/constants/imageConstants.js";

const router = useRouter();
const callRoutes = ref(null);
const isLoading = ref(false);
const routeToManipulate = ref(null);
const showModalToDelete = ref(false);
const pagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0,
});

const callRoutesActions = computed(() => [
  {
    label: "Edit Route",
    handler: (row) => handleRouteEdit(row),
    icon: imageNames.EDIT,
  },
  {
    label: "Delete Route",
    handler: (row) => handleRouteDelete(row),
    icon: imageNames.DELETE_GREY,
  },
]);

const handlePageChange = (newPagination) => {
  Object.assign(pagination, newPagination);
};

const handleCreateRoute = () => {
  router.push({ name: "create-route" });
};

const handleRouteEdit = (row) => {
  router.push({
    name: "update-route",
    params: { id: row?.id },
  });
};
const handleRouteDelete = (row) => {
  routeToManipulate.value = row;
  showModalToDelete.value = true;
};

const toGetAllCallRoutes = async () => {
  try {
    isLoading.value = true;

    const response = await CallService.getAllCallRoutes();
    if (response?.success) {
      callRoutes.value = response?.routes.map((route) => ({
        ...route,
        phone_number: route?.phone_number?.phone_number,
      }));
    }
  } catch (error) {
    console.error("An error occurred while fetching call routes: ", error);
  } finally {
    isLoading.value = false;
  }
};

const toDeleteCallRoute = async () => {
  try {
    const response = await CallService.deleteCallRoute(
      routeToManipulate.value?.id,
    );
    if (response?.success) {
      showModalToDelete.value = false;
      await toGetAllCallRoutes();
      message.success("Call Route was deleted successfully!!");
    }
  } catch (error) {
    console.error("An error occurred while deleting call route: ", error);
    message.error("Call Route could not be deleted!!");
  }
};

onMounted(() => {
  toGetAllCallRoutes();
});
</script>
